import { ValidationError } from './Hours/types';
import {
  validateDeliveryHours,
  validateOverlappingPeriods,
  validateSameStartEndTime,
} from './Hours/validation';
import { Schedule } from './types';

// helper function to merge delivery errors when the same period fails multiple validations
const mergeDeliveryErrors = (
  sameTimeErrors: ValidationError[],
  withinPickupErrors: ValidationError[],
): ValidationError[] => {
  const errorsByDay = new Map<string | number, ValidationError>();

  // process "within pickup" errors first
  withinPickupErrors.forEach(error => {
    errorsByDay.set(error.dayIndex, error);
  });

  // process "same time" errors and merge with existing errors if they share periods
  sameTimeErrors.forEach(sameTimeError => {
    const existingError = errorsByDay.get(sameTimeError.dayIndex);

    if (existingError) {
      // find periods that exist in both errors
      const sharedPeriodIds = new Set(
        sameTimeError.periods
          .filter(p1 => existingError.periods.some(p2 => p2.id === p1.id))
          .map(p => p.id),
      );

      // separate shared and unique periods
      const sharedPeriods = existingError.periods.filter(p => sharedPeriodIds.has(p.id));
      const uniqueWithinPickupPeriods = existingError.periods.filter(
        p => !sharedPeriodIds.has(p.id),
      );
      const uniqueSameTimePeriods = sameTimeError.periods.filter(p => !sharedPeriodIds.has(p.id));

      // create separate errors for different scenarios
      const errors: ValidationError[] = [];

      // shared periods get combined messages
      if (sharedPeriods.length > 0) {
        errors.push({
          dayIndex: sameTimeError.dayIndex,
          messages: [...new Set([...existingError.messages, ...sameTimeError.messages])],
          periods: sharedPeriods,
          type: 'delivery',
        });
      }

      // unique "within pickup" periods keep their messages
      if (uniqueWithinPickupPeriods.length > 0) {
        errors.push({
          dayIndex: existingError.dayIndex,
          messages: existingError.messages,
          periods: uniqueWithinPickupPeriods,
          type: 'delivery',
        });
      }

      // unique "same time" periods keep their messages
      if (uniqueSameTimePeriods.length > 0) {
        errors.push({
          dayIndex: sameTimeError.dayIndex,
          messages: sameTimeError.messages,
          periods: uniqueSameTimePeriods,
          type: 'delivery',
        });
      }

      // replace the existing error with merged results
      errorsByDay.delete(sameTimeError.dayIndex);
      errors.forEach((error, index) => {
        errorsByDay.set(`${error.dayIndex}-${index}`, error);
      });
    } else {
      // no existing error for this day, just add the same time error
      errorsByDay.set(sameTimeError.dayIndex, sameTimeError);
    }
  });

  return Array.from(errorsByDay.values());
};

export const validateMenuSchedules = (
  transformedSchedules: Schedule[],
  transformedDeliveryHours: Schedule[],
): ValidationError[] => {
  const allErrors: ValidationError[] = [];

  // check for same start and end times in pickup hours
  const pickupSameTimeErrors = validateSameStartEndTime(transformedSchedules, 'pickup');
  allErrors.push(...pickupSameTimeErrors);

  // check for overlapping periods in pickup hours
  const pickupErrors = validateOverlappingPeriods(transformedSchedules, 'pickup');
  allErrors.push(...pickupErrors);

  // check for same start and end times in delivery hours
  const deliverySameTimeErrors = validateSameStartEndTime(transformedDeliveryHours, 'delivery');

  const deliveryErrors = validateDeliveryHours(transformedSchedules, transformedDeliveryHours);

  // merge errors when same period has both "same time" & "within pickup" failures
  const mergedDeliveryErrors = mergeDeliveryErrors(deliverySameTimeErrors, deliveryErrors);
  allErrors.push(...mergedDeliveryErrors);

  return allErrors;
};
